import { useMemo, useRef, useState } from "react";
import { Calendar, Plus, Check, X, RotateCcw, Star, ShoppingCart, ChevronDown } from "react-feather";
import { OverflowMenu } from "../components/OverflowMenu";
import { ShopDaysModal, type ShopDaysModalRef } from "../components/ShopDaysModal";
import { ShoppingListSwitcher } from "../components/ShoppingListSwitcher";
import { useToast } from "../components/Toast";
import {
  selectAllShoppingItems,
  selectGroupedShoppingItems,
  selectShoppingItemsByRecipe,
  selectHasAisleOverrides,
  selectShoppingGroupBy,
  selectActiveListId,
  selectActiveList,
  setShoppingGroupBy,
  addShoppingItem,
  setShoppingItemCategory,
  resetAllShoppingCategories,
  toggleShoppingItem,
  removeShoppingItem,
  clearCheckedShoppingItems,
  clearAllShoppingItems,
  restoreShoppingItems,
  selectWeekStartsOn,
  selectUnitSystem,
  formatShoppingQuantity,
  shoppingListDisplayName,
  categoriseShoppingItem,
  SHOPPING_CATEGORIES,
  useT,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfWeek = (d: Date, weekStartsOn: number) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() - weekStartsOn + 7) % 7));
  return x;
};

export default function ShoppingList() {
  const t = useT();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const listId = useAppSelector(selectActiveListId);
  const activeList = useAppSelector(selectActiveList);
  const items = useAppSelector(selectAllShoppingItems);
  const aisleGroups = useAppSelector(selectGroupedShoppingItems);
  const recipeGroups = useAppSelector(selectShoppingItemsByRecipe);
  const groupBy = useAppSelector(selectShoppingGroupBy);
  const unitSystem = useAppSelector(selectUnitSystem);
  const hasOverrides = useAppSelector(selectHasAisleOverrides);
  const weekStartsOn = useAppSelector(selectWeekStartsOn);

  // Unified {key,label,items} shape for whichever grouping is active.
  const displayGroups =
    groupBy === "recipe"
      ? recipeGroups.map((g) => ({ key: g.key, label: g.title, items: g.items }))
      : aisleGroups.map((g) => ({ key: g.key, label: g.label, items: g.items }));

  const shopModalRef = useRef<ShopDaysModalRef>(null);
  const todayYmd = ymd(new Date());

  // This week's seven days — the picker ticks the days that have meals planned.
  const weekDays = useMemo(() => {
    const start = startOfWeek(new Date(), weekStartsOn);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [weekStartsOn]);

  const [draft, setDraft] = useState("");
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // Ticked-off progress.
  const total = items.length;
  const checked = items.filter((i) => i.checked).length;

  const addManual = () => {
    const name = draft.trim();
    if (!name || !listId) return;
    dispatch(addShoppingItem({ name, listId }));
    setDraft("");
  };

  // Clear, capturing the affected ids so the toast can offer an Undo.
  const clearWithUndo = (ids: string[], clear: () => void) => {
    if (ids.length === 0) return;
    clear();
    toast.show(t("shopping.cleared", { count: ids.length }), {
      label: t("common.undo"),
      onClick: () => dispatch(restoreShoppingItems(ids)),
    });
  };

  const clearChecked = () =>
    listId &&
    clearWithUndo(
      items.filter((i) => i.checked).map((i) => i.id),
      () => dispatch(clearCheckedShoppingItems({ listId })),
    );

  const handleClearAll = () => {
    if (!listId) return;
    if (window.confirm(t("shopping.clearConfirmWeb"))) {
      clearWithUndo(items.map((i) => i.id), () => dispatch(clearAllShoppingItems({ listId })));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="font-display text-3xl font-extrabold text-ink truncate">{t("shopping.title")}</h1>
        <OverflowMenu
          buttonClassName="w-9 h-9 flex items-center justify-center rounded-lg text-ink-soft hover:bg-surface-2 cursor-pointer shrink-0"
          items={[
            { label: t("shopping.manageLists"), onClick: () => setSwitcherOpen(true) },
            ...(items.length > 0
              ? [
                  { label: t("shopping.clearTicked"), onClick: clearChecked },
                  ...(hasOverrides
                    ? [
                        {
                          label: t("shopping.resetAisles"),
                          onClick: () => {
                            if (listId) dispatch(resetAllShoppingCategories({ listId }));
                            toast.show(t("shopping.aislesReset"));
                          },
                        },
                      ]
                    : []),
                  { label: t("shopping.clearAll"), destructive: true, onClick: handleClearAll },
                ]
              : []),
          ]}
        />
      </div>

      {/* Active list switcher — switch / create / rename / delete lists. */}
      <button
        onClick={() => setSwitcherOpen(true)}
        className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-surface-2 hover:bg-line transition-colors cursor-pointer"
      >
        <ShoppingCart className="w-3.5 h-3.5 text-terracotta" />
        <span className="text-sm font-bold text-ink-soft max-w-[240px] truncate">
          {shoppingListDisplayName(activeList?.name, t)}
        </span>
        <ChevronDown className="w-4 h-4 text-ink-faint" />
      </button>

      <ShoppingListSwitcher open={switcherOpen} onClose={() => setSwitcherOpen(false)} />

      {/* Add from meal plan */}
      <button
        onClick={() => shopModalRef.current?.open()}
        className="w-full flex items-center justify-center gap-2 bg-terracotta/10 hover:bg-terracotta/15 transition-colors rounded-2xl py-3.5 mb-3 cursor-pointer"
      >
        <Calendar className="w-[18px] h-[18px] text-terracotta" />
        <span className="text-base font-bold text-terracotta-ink">{t("shopping.addFromPlanner")}</span>
      </button>

      {/* Manual add */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          addManual();
        }}
        className="flex items-center gap-2 mb-2"
      >
        <div className="flex-1 flex items-center gap-2 bg-surface border border-line rounded-xl px-3 py-2.5 focus-within:border-terracotta transition-colors">
          <Plus className="w-4 h-4 text-ink-faint shrink-0" />
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("shopping.addItemPlaceholder")}
            className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
          />
        </div>
        {draft.trim().length > 0 && (
          <button
            type="submit"
            className="px-4 py-2.5 rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors text-white text-sm font-bold cursor-pointer"
          >
            {t("shopping.addItem")}
          </button>
        )}
      </form>


      {/* Ticked-off progress. */}
      {total > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-ink-faint mb-1.5">
            {t("shopping.tickedOff", { checked, total })}
          </p>
          <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
            <div
              className="h-full rounded-full bg-terracotta transition-all"
              style={{ width: `${Math.round((checked / total) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Group-by toggle — remembers the choice as the default. */}
      {items.length > 0 && (
        <div className="flex bg-surface-2 rounded-xl p-1 mb-4 w-full max-w-xs">
          {(["aisle", "recipe"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => dispatch(setShoppingGroupBy(mode))}
              className={`flex-1 rounded-lg py-1.5 text-sm font-semibold transition-colors cursor-pointer ${
                groupBy === mode
                  ? "bg-surface text-terracotta-ink shadow-sm"
                  : "text-ink-soft hover:text-ink"
              }`}
            >
              {mode === "aisle" ? t("shopping.byAisle") : t("shopping.byRecipe")}
            </button>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-ink-faint text-sm mt-6">
          {t("shopping.empty")}
        </p>
      ) : (
        <div className="mt-4">
          {displayGroups.map((group) => (
            <section key={group.key} className="mb-2">
              {/* Don't label an aisle list that's all one uncategorised "Other" group. */}
              {!(groupBy === "aisle" && displayGroups.length === 1 && group.key === "other") && (
                <h2
                  className={
                    groupBy === "recipe"
                      ? "text-sm font-bold text-terracotta-ink mt-4 mb-1"
                      : "text-[11px] font-bold uppercase tracking-wider text-terracotta-ink mt-4 mb-1"
                  }
                >
                  {group.label}
                </h2>
              )}
              <ul className="bg-surface rounded-2xl border border-line divide-y divide-line overflow-hidden">
                {group.items.map((item) => (
                  <li key={item.id} className="flex items-center gap-3 px-4 py-3">
                    <button
                      onClick={() => dispatch(toggleShoppingItem(item.id))}
                      className="flex-1 flex items-center gap-3 min-w-0 text-left cursor-pointer group"
                    >
                      <span
                        className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                          item.checked
                            ? "bg-terracotta border-terracotta"
                            : "border-line group-hover:border-terracotta"
                        }`}
                      >
                        {item.checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={`block text-[15px] font-semibold ${
                            item.checked ? "line-through text-ink-faint" : "text-ink"
                          }`}
                        >
                          {item.name}
                        </span>
                        {(() => {
                          const qty = formatShoppingQuantity(item, unitSystem, t);
                          return qty ? (
                            <span
                              className={`block text-sm ${
                                item.checked ? "line-through text-ink-faint/70" : "text-ink-soft"
                              }`}
                            >
                              {qty}
                            </span>
                          ) : null;
                        })()}
                      </span>
                    </button>
                    {/* Shared-ingredient marker: star + how many recipes use it. */}
                    {(item.recipeIds?.length ?? 0) > 1 && (
                      <span
                        className="shrink-0 flex items-center gap-0.5 text-terracotta-ink"
                        title={t("shopping.usedInRecipes", { count: item.recipeIds!.length })}
                      >
                        <Star className="w-3.5 h-3.5 fill-current" />
                        <span className="text-xs font-bold">{item.recipeIds!.length}</span>
                      </span>
                    )}
                    {/* Aisle move only makes sense when grouped by aisle. */}
                    {groupBy === "aisle" && (
                      <OverflowMenu
                        label={t("shopping.moveToAisle")}
                        buttonClassName="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink hover:bg-surface-2 cursor-pointer"
                        items={[
                          ...SHOPPING_CATEGORIES.map((c) => ({
                            label: c.label,
                            icon:
                              c.key ===
                              (item.category ?? categoriseShoppingItem(item.name)) ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <span className="w-4 h-4" />
                              ),
                            onClick: () =>
                              dispatch(
                                setShoppingItemCategory({ id: item.id, category: c.key }),
                              ),
                          })),
                          {
                            label: t("shopping.resetToAuto"),
                            icon: <RotateCcw className="w-4 h-4" />,
                            onClick: () =>
                              dispatch(
                                setShoppingItemCategory({ id: item.id, category: null }),
                              ),
                          },
                        ]}
                      />
                    )}
                    <button
                      onClick={() => dispatch(removeShoppingItem(item.id))}
                      aria-label={t("common.remove")}
                      className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink hover:bg-surface-2 cursor-pointer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <ShopDaysModal ref={shopModalRef} days={weekDays} todayYmd={todayYmd} />
    </div>
  );
}
